import type { GetAllCatalogProductGridItemsQuery } from './getAllCatalogProductGridItems.graphql.generated';
import {
  useGetAllCatalogProductGridItemsQuery as useGetCatalogProductsQuery,
  useGetAllCatalogProductGridItemsLazyQuery as useGetCatalogProductsLazyQuery,
} from './getAllCatalogProductGridItems.graphql.generated';
import { ProductUniversalItemSource } from '@graphql/generated-base';
import { useMemo } from 'react';
import type {
  GetAllGridItemsEdge,
  GetAllGridItemsLazyQueryOptions,
  GetAllGridItemsLazyQueryResult,
  GetAllGridItemsQueryData,
  GetAllGridItemsQueryOptions,
  GetAllGridItemsQueryResult,
} from './getAllProductGridItems';
import type { CatalogItemTile } from '../../../Catalog.types';

const extractCatalogItems = (fallbackCatalogData: GetAllCatalogProductGridItemsQuery | undefined): CatalogItemTile[] => {
  return fallbackCatalogData?.productCatalogSearchResults?.pagedResults?.edges.map(convertCatalogItem).filter((item): item is CatalogItemTile => Boolean(item)) ?? [];
};

const convertCatalogItem = (edge: NonNullable<GetAllCatalogProductGridItemsQuery['productCatalogSearchResults']['pagedResults']>['edges'][0]): CatalogItemTile | null => {
  if (edge.node.catalogItem) {
    return {
      ...edge.node.catalogItem,
      source: ProductUniversalItemSource.catalogSearch,
    };
  }
  return null;
};

const convertResponseData = (data?: GetAllCatalogProductGridItemsQuery): GetAllGridItemsQueryData | undefined => {
  if (!data?.productCatalogSearchResults) {
    return undefined;
  }
  const pagedResults = data.productCatalogSearchResults?.pagedResults;
  return {
    productCatalogCategoryTreeRoot: data.productCatalogCategoryTreeRoot,
    productUniversalSearchResults: {
      allFilters: data.productCatalogSearchResults?.allFilters,
      pagedResults: pagedResults
        ? {
            edges: pagedResults.edges
              .map(edge => ({
                cursor: edge.cursor,
                node: convertCatalogItem(edge),
              }))
              .filter((edge): edge is GetAllGridItemsEdge<CatalogItemTile> => Boolean(edge.node)),
            pageInfo: pagedResults.pageInfo,
          }
        : undefined,
    },
  };
};

export type GetAllCatalogProductQueryResult = Merge<
  GetAllGridItemsQueryResult,
  {
    productList: CatalogItemTile[];
  }
>;

export const useGetCatalogProductGridItemsQuery = (options: GetAllGridItemsQueryOptions): GetAllCatalogProductQueryResult => {
  const { data, ...restQueryReturn } = useGetCatalogProductsQuery(options);

  const productList = useMemo(() => {
    return extractCatalogItems(data);
  }, [data]);

  return {
    data: convertResponseData(data),
    productList,
    ...restQueryReturn,
  } as const;
};

export type GetAllCatalogProductLazyQueryResult = Merge<
  GetAllGridItemsLazyQueryOptions,
  {
    fallbackProductList: CatalogItemTile[];
  }
>;

export const useGetCatalogProductGridItemsLazyQuery = (options: GetAllGridItemsLazyQueryOptions): GetAllGridItemsLazyQueryResult => {
  const [sendRequest, { data, ...restQueryReturn }] = useGetCatalogProductsLazyQuery(options);

  const fallbackProductList = useMemo(() => {
    return extractCatalogItems(data);
  }, [data]);

  return {
    sendRequest,
    fallbackProductList,
    ...restQueryReturn,
  } as const;
};
